<template>
  <dx-modal
    id="invoice-detail-modal"
    size="lg"
    title="Nota Fiscal de Serviço"
    :value="show"
    @input="close"
  >
    <div class="loading loading-lg" v-if="loading"></div>
    <template v-else-if="data">
      <div class="columns">
        <div class="column col-2 form-group">
          <label class="form-label text-small">Número da nota</label>
          <div class="text-bold">{{ data.code || '--' }}</div>
        </div>
        <div class="column col-7 form-group">
          <label class="form-label text-small">Chave de acesso</label>
          <div style="word-break: break-all">{{ data.accessKey || '--' }}</div>
        </div>
        <div class="column col-3 form-group">
          <label class="form-label text-small">Situação</label>
          <div class="text-bold" :class="statusTextClass">
            <fa-icon
              v-if="isProcessing"
              :icon="['fal', 'spinner-third']"
              :spin="true"
              class="mr-1"
            />{{ statusName }}
          </div>
        </div>
      </div>

      <div
        v-if="data.meta && data.meta.statusReason"
        class="alert"
        :class="isStatusError ? 'alert-error' : 'alert-info'"
      >
        <div class="text-pre">{{ data.meta.statusReason.trim() }}</div>
      </div>

      <div class="divider" />

      <div class="columns">
        <div class="column col-4 form-group">
          <label class="form-label text-small">Tomador de serviços</label>
          <div class="text-primary text-bold">{{ data.entity.name }}</div>
        </div>
        <div class="column col-3 form-group">
          <label class="form-label text-small">Documento</label>
          <div class="">{{ data.entity.identity | identity }}</div>
        </div>
        <div class="column col-3 form-group">
          <label class="form-label text-small">Email</label>
          <div class="">{{ data.entity.email }}</div>
        </div>
      </div>

      <div class="divider" />

      <div class="columns">
        <div class="column col-3 form-group">
          <label class="form-label">Data de emissão</label>
          <dx-input-date
            :value="data.createdAt"
            :readonly="true"
          />
        </div>
        <div class="column col-3 form-group">
          <label class="form-label">Valor da nota fiscal</label>
          <div class="input-group">
            <span class="input-group-addon">R$</span>
            <dx-input-number
              :precision="2"
              :value="data.totalAmount"
              :readonly="true"
            />
          </div>
        </div>
        <div class="column col-3 form-group">
          <label class="form-label">Alíquota de ISS</label>
          <div class="input-group">
            <span class="input-group-addon">%</span>
            <dx-input-number
              :precision="2"
              :value="data.service.issRate"
              :readonly="true"
            />
          </div>
        </div>
        <div class="column col-3 form-group">
          <label class="form-label">Deduções</label>
          <div class="input-group">
            <span class="input-group-addon">R$</span>
            <dx-input-number
              :precision="2"
              :value="data.deductions || 0"
              :readonly="true"
            />
          </div>
        </div>
      </div>
      <div class="columns">
        <div class="column col-6 form-group">
          <label class="form-label">Descrição da nota</label>
          <textarea
            class="form-input"
            rows="3"
            :value="data.service.description"
            :readonly="true"
          />
        </div>
        <div class="column col-6 form-group">
          <label class="form-label">Observações</label>
          <textarea
            class="form-input"
            rows="3"
            :value="data.observations"
            :readonly="true"
          />
        </div>
      </div>
    </template>

    <template v-slot:footer>
      <button
        v-if="isCancellable"
        class="btn btn-error btn-icon btn-icon-left mr-2 float-left"
        :class="{ loading: cancelling }"
        @click="confirmCancel"
        :disabled="cancelling"
      ><fa-icon :icon="['fal', 'ban']" /> Cancelar nota</button>
      <button
        v-if="isPrintable"
        class="btn btn-info-outline btn-icon btn-icon-left mr-2"
        :class="{ loading: downloading }"
        @click="download"
        :disabled="downloading"
      ><fa-icon :icon="['fal', 'cloud-download']" /> Baixar XML</button>
      <button
        v-if="isPrintable"
        class="btn btn-info btn-icon btn-icon-left mr-2"
        :class="{ loading: printing }"
        @click="print"
        :disabled="printing"
      ><fa-icon :icon="['fal', 'print']" /> Imprimir Nota Fiscal</button>
      <button
        v-if="isRegenerable"
        class="btn btn-primary btn-icon btn-icon-left mr-2"
        @click="edit"
      ><fa-icon :icon="['fal', 'redo']" /> {{ regenerateLabel }}</button>
      <button class="btn" @click="close">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import {
  CREATE_FISCAL_DOCUMENT,
  DELETE_FISCAL_DOCUMENT,
} from '@/data/actions/modules/financial';
import {
  CANCELLED,
  ISSUED,
  REJECTED,
  FAILED,
  CANCELLATION_REJECTED,
  CANCELLATION_FAILED,
  isTransient,
  getName as getStatusName,
  getTextClass as getStatusTextClass,
} from './statuses';

// Enquanto a nota está em estado transitório, recarrega em segundo plano até
// atingir um estado final (ou esgotar as tentativas).
const POLL_INTERVAL = 4000;
const MAX_POLL_ATTEMPTS = 15;

export default {
  props: {
    show: {
      type: Boolean,
    },
    id: {
      type: String,
    },
  },
  data() {
    return {
      data: null,
      loading: false,
      printing: false,
      downloading: false,
      cancelling: false,
      pollTimer: null,
      pollAttempts: 0,
    };
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    this.clearPoll();
  },
  computed: {
    statusName() {
      return this.data ? getStatusName(this.data.status) : '';
    },
    statusTextClass() {
      return this.data ? getStatusTextClass(this.data.status) : '';
    },
    isPrintable() {
      return this.data && this.data.status === ISSUED;
    },
    isCancellable() {
      // Emitida, ou um cancelamento anterior que falhou/foi rejeitado — a nota
      // segue válida e o cancelamento pode ser (re)tentado.
      return !!this.data
        && [ISSUED, CANCELLATION_REJECTED, CANCELLATION_FAILED].includes(this.data.status);
    },
    isRegenerable() {
      // Sem nota válida (cancelada, rejeitada ou falha na emissão): permite
      // reabrir a tela de criação para tentar novamente.
      return !!this.data && [CANCELLED, REJECTED, FAILED].includes(this.data.status);
    },
    regenerateLabel() {
      return this.data && this.data.status === CANCELLED
        ? 'Gerar novamente'
        : 'Tentar novamente';
    },
    isProcessing() {
      return !!this.data && isTransient(this.data.status);
    },
    canPrint() {
      return true;
    },
    canCreate() {
      return this.$can(CREATE_FISCAL_DOCUMENT);
    },
    canCancel() {
      return this.$can(DELETE_FISCAL_DOCUMENT);
    },
    isStatusError() {
      return this.data && [
        REJECTED,
        FAILED,
        CANCELLATION_REJECTED,
        CANCELLATION_FAILED,
      ].includes(this.data.status);
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.pollAttempts = 0;
      await this.fetch();
      this.loading = false;
      this.schedulePoll();
    },
    async fetch(silent = false) {
      try {
        const { data } = await this.$http.get(`/fiscal-documents/${this.id}`);
        this.data = data;
      } catch (e) {
        // Em polling silencioso não incomoda o usuário: apenas tenta de novo.
        if (!silent) this.$toast.error(e);
      }
    },
    async refresh() {
      this.pollAttempts += 1;
      await this.fetch(true);
      this.schedulePoll();
    },
    schedulePoll() {
      this.clearPoll();
      if (this.isProcessing && this.pollAttempts < MAX_POLL_ATTEMPTS) {
        this.pollTimer = setTimeout(this.refresh, POLL_INTERVAL);
      }
    },
    clearPoll() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async print() {
      if (!this.canPrint) {
        this.$toast.show('Acesso não autorizado!', { type: 'error' });
        return;
      }

      if (!this.isPrintable || this.printing) return;

      this.printing = true;
      try {
        await this.$file.print(`/fiscal-documents/${this.data.id}/print`);
      } catch (e) {
        this.$toast.error(e);
      }
      this.printing = false;
    },
    async download() {
      if (!this.canPrint) {
        this.$toast.show('Acesso não autorizado!', { type: 'error' });
        return;
      }

      if (!this.isPrintable || this.downloading) return;

      this.downloading = true;
      try {
        await this.$file.download(`/fiscal-documents/${this.data.id}/xml`, {}, {
          name: `NFS-e-${this.data.code}.xml`,
        });
      } catch (e) {
        this.$toast.error(e);
      }
      this.downloading = false;
    },
    confirmCancel() {
      if (!this.canCancel) {
        this.$toast.show('Acesso não autorizado!', { type: 'error' });
        return;
      }

      this.$dialog.show('Cancelar nota fiscal', {
        type: 'danger',
        html: 'Tem certeza que deseja cancelar esta nota fiscal? '
          + 'O cancelamento é enviado à Receita e não pode ser desfeito.',
        buttons: [
          { label: 'Voltar', click: close => close() },
          {
            label: 'Cancelar nota',
            classes: 'btn-error ml-2',
            click: (close) => {
              close();
              this.cancel();
            },
          },
        ],
      });
    },
    async cancel() {
      if (this.cancelling) return;

      this.cancelling = true;
      try {
        await this.$http.post(`/fiscal-documents/${this.data.id}/cancel`);
        // Cancelamento assíncrono: a nota entra em "cancelando" e o resultado é
        // acompanhado pelo polling (cancelada / falha no cancelamento).
        this.$toast.success('Cancelamento solicitado');
        await this.load();
      } catch (e) {
        this.$toast.error(e);
      }
      this.cancelling = false;
    },
    edit() {
      if (!this.canCreate) {
        this.$toast.show('Acesso não autorizado!', { type: 'error' });
        return;
      }

      // Reabre a tela de criação com os dados desta nota para ajuste antes de
      // reemitir (ex.: corrigir imposto/valor) — em vez de reemitir direto.
      this.$emit('edit', this.data);
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>
